// A native <select> and <option> cannot be driven from another element with
// aria-activedescendant, which is the whole pattern here: focus stays in the editor and the list
// follows it. So the listbox and its options are roles on plain elements.
/* oxlint-disable jsx-a11y/prefer-tag-over-role */
import { computePosition, flip, offset, shift } from "@floating-ui/dom";
import { getExtensionDependencyFromEditor } from "@lexical/extension";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect, useRef, useSyncExternalStore, type JSX, type MouseEvent } from "react";
import { SuggestionsExtension } from "../features/suggestions/extension";
import type { EditorLabels } from "./labels";

/** IDs are per editor, so two editors on one page never point at each other's options. */
export const suggestionListId = (editorKey: string): string => `ze-suggestions-${editorKey}`;
export const suggestionOptionId = (editorKey: string, index: number): string =>
  `ze-suggestion-${editorKey}-${index}`;

// Rows act on the editor's selection, so pressing one must not take focus from it.
const keepSelection = (event: MouseEvent): void => event.preventDefault();

/**
 * The list under the caret. Focus never leaves the editor: the editable element points at this
 * list with `aria-controls` and at the active row with `aria-activedescendant`, which is how a
 * screen reader follows the arrow keys without the caret moving.
 */
export function SuggestionMenu({ labels }: { labels: EditorLabels }): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const { engine, accept } = getExtensionDependencyFromEditor(editor, SuggestionsExtension).output;
  const state = useSyncExternalStore(engine.subscribe, engine.getState, engine.getState);
  const menuRef = useRef<HTMLDivElement>(null);
  const { open, items, activeIndex, status } = state;

  useEffect(() => {
    if (!open) return;
    const place = (): void => {
      const menu = menuRef.current;
      const root = editor.getRootElement();
      const selection = window.getSelection();
      if (menu === null || root === null || selection === null || selection.rangeCount === 0)
        return;
      const range = selection.getRangeAt(0);
      if (!root.contains(range.commonAncestorContainer)) return;
      // A collapsed range has no box in some engines; the element holding the caret always does.
      let rect = range.getClientRects()[0] ?? range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        const holder = range.startContainer;
        const element = holder instanceof Element ? holder : holder.parentElement;
        if (element !== null) rect = element.getBoundingClientRect();
      }
      const reference = { getBoundingClientRect: () => rect, contextElement: root };
      void computePosition(reference, menu, {
        placement: "bottom-start",
        strategy: "fixed",
        middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        menu.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
        menu.dataset.placed = "true";
      });
    };
    place();
    window.addEventListener("scroll", place, { capture: true, passive: true });
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, { capture: true });
      window.removeEventListener("resize", place);
    };
  }, [editor, open, state.query, items.length]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  if (!open) return null;
  const key = editor.getKey();
  const note =
    status === "error"
      ? labels.suggestionsError
      : status === "loading"
        ? labels.suggestionsLoading
        : items.length === 0
          ? labels.suggestionsEmpty
          : null;

  return (
    <div
      ref={menuRef}
      role="presentation"
      className="ze-suggestions"
      data-status={status}
      onMouseDown={keepSelection}
    >
      <div id={suggestionListId(key)} role="listbox" aria-label={labels.suggestions}>
        {items.map((item, index) => (
          // The keyboard reaches a row through the editor's own key commands (arrows, Enter, Tab,
          // Escape), so rows take no focus and need no key handlers of their own.
          // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
          <div
            key={item.key}
            id={suggestionOptionId(key, index)}
            role="option"
            aria-selected={index === activeIndex}
            className="ze-suggestion"
            onMouseEnter={() => engine.setActive(index)}
            onClick={() => accept(index)}
          >
            <span className="ze-suggestion-label">{item.label}</span>
            {item.description !== undefined && (
              <span className="ze-suggestion-description">{item.description}</span>
            )}
          </div>
        ))}
      </div>
      {/* One polite message for the state of the search, not an announcement per keystroke. */}
      <output className="ze-suggestions-note">{note}</output>
    </div>
  );
}

/** The ARIA wiring for the editable element while the menu is open. */
export function useSuggestionAria(): {
  ariaAutoComplete: "list" | "none";
  ariaControls: string | undefined;
  ariaActiveDescendant: string | undefined;
} {
  const [editor] = useLexicalComposerContext();
  const { engine } = getExtensionDependencyFromEditor(editor, SuggestionsExtension).output;
  const state = useSyncExternalStore(engine.subscribe, engine.getState, engine.getState);
  const key = editor.getKey();
  const active = state.open && state.items.length > 0;
  return {
    ariaAutoComplete: engine.rules.length > 0 ? "list" : "none",
    ariaControls: state.open ? suggestionListId(key) : undefined,
    ariaActiveDescendant: active ? suggestionOptionId(key, state.activeIndex) : undefined,
  };
}
